import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";
import { servicesApiCollections } from "src/app/core/constants/api_collection";
import { ApiService } from "src/app/shared/services/api.service";
import { Cart } from "../../q-commerce/models/cart.model";
import { Variant } from "../../q-commerce/models/category-group.model";
import { MainCategory } from "../../q-commerce/models/main-category.model";
import { BookingType } from "src/app/core/constants/bookingConstant";
import { Merchant } from "../../e-delivery/models/merchant.model";

@Injectable({
  providedIn: "root"
})
export class ServicesService {
  // BehaviourSubject to hold the city setting
  private citySettingSubject = new BehaviorSubject<any>(null);

  // Observable to expose the city setting
  citySettingSubject$ = this.citySettingSubject.asObservable();

  // BehaviorSubject to hold the selected booking type
  private bookingType = new BehaviorSubject<number>(BookingType.SERVICE_AT_HOME);

  // Observable to expose the selected booking type
  bookingType$ = this.bookingType.asObservable();

  // Use a BehaviorSubject to store the cart items
  private cartItems = new BehaviorSubject<Cart[]>([]);

  // Observable to expose the cart items
  cartItems$ = this.cartItems.asObservable();

  // BehaviourSubject to hold the selected cart merchant
  private selectedCartMerchantSubject = new BehaviorSubject<Merchant | null>(null);

  //Observable to expose the selected cart merchant
  selectedCartMerchant$ = this.selectedCartMerchantSubject.asObservable();

  constructor(private apiService: ApiService) {}

  /**
   * Fetches the list of main categories using the ApiService.
   * Subscribes to the service's observable to receive the category data, updates the observer with the response data and completes the observable.
   * If there is an error, it updates the observer with false and completes the observable.
   * @param parameters The parameters to be passed as query parameters to the API.
   * @returns An observable of the category data.
   */
  getMainCategoryList(parameters: any): Observable<any> {
    return new Observable<any>((observer) => {
      this.apiService.get({ url: servicesApiCollections.mainCategory, parameters }).subscribe({
        next: (response) => {
          if (response.data) {
            observer.next(response.data.data);
            observer.complete();
          } else {
            observer.next(false);
            observer.complete();
          }
        },
        error: () => {
          observer.next(false);
          observer.complete();
        }
      });
    });
  }

  /**
   * Checks if a business is available for ordering using the ApiService.
   * Subscribes to the service's observable to receive the response data, updates the observer with the response data and completes the observable.
   * If there is an error, it updates the observer with false and completes the observable.
   * @param parameters The parameters to be passed as query parameters to the API.
   * @returns An observable of the response data.
   */
  checkServiceBusiness(parameters: any): Observable<any> {
    return new Observable<any>((observer) => {
      const url = `${servicesApiCollections.checkBusiness}`;
      this.apiService.post({ url: url, parameters }).subscribe({
        next: (response) => {
          if (response.data) {
            observer.next(response.data.data);
            observer.complete();
          } else {
            observer.next(false);
            observer.complete();
          }
        },
        error: () => {
          observer.next(false);
          observer.complete();
        }
      });
    });
  }

  /**
   * Fetches the list of groups using the ApiService.
   * Subscribes to the service's observable to receive the group data, updates the observer with the response data and completes the observable.
   * If there is an error, it updates the observer with false and completes the observable.
   * @param parameters The parameters to be passed as query parameters to the API.
   * @returns An observable of the group data.
   */
  getGroupList(parameters: any): Observable<any> {
    return new Observable<any>((observer) => {
      const url = `${servicesApiCollections.group}`;
      this.apiService.get({ url, parameters }).subscribe({
        next: (response) => {
          if (response.data) {
            observer.next(response.data.data);
            observer.complete();
          } else {
            observer.next(false);
            observer.complete();
          }
        },
        error: () => {
          observer.next(false);
          observer.complete();
        }
      });
    });
  }

  /**
   * Updates the current city setting with the provided value.
   * This will notify all subscribers of the citySettingSubject$ observable.
   * @param citySetting The new city setting to be applied.
   */
  updateCitySetting(citySetting: any): void {
    this.citySettingSubject.next(citySetting);
  }

  /**
   * Updates the selected booking type observable with the given booking type.
   * This will cause all subscribers to the bookingType$ observable to receive the new booking type.
   * @param bookingType The booking type to select as the current booking type.
   */
  updateBookingType(bookingType: number): void {
    this.bookingType.next(bookingType);
  }

  /**
   * Updates the current state of the cart with the given array of `Cart` items.
   * This will cause all subscribers to the `cartItems$` observable to receive the new cart items.
   * @param cartItems The array of `Cart` items to update the cart with.
   */
  updateCartItems(cartItems: Cart[]): void {
    this.cartItems.next(cartItems);
  }

  /**
   * Adds a product variant to the cart. If the product variant is already present in the cart,
   * the quantity will be incremented by the given quantity, up to the maximum allowed in the cart.
   * Otherwise, the product variant will be added with an initial quantity of 1.
   *
   * @param item - The cart item to add to the cart.
   * @param variant - The product variant to add to the cart.
   */
  addToCart(item: Cart, variant: Variant, serviceMainCategory: MainCategory): void {
    const currentCart = this.cartItems.value.length ? [...this.cartItems.value] : [...JSON.parse(localStorage.getItem("serviceCartItems") || "[]")];
    const existingItem = currentCart.find(
      (cartItem) => cartItem.productId === item.productId && cartItem.variantId === item.variantId && JSON.stringify(item.modifiers) === JSON.stringify(variant.modifiers)
    );
    if (existingItem) {
      existingItem.qty = Math.min(existingItem.qty + item.qty, variant.maxQtyAddInCart);
    } else {
      item.qty = Math.min(item.qty, variant.maxQtyAddInCart);
      currentCart.push(item);
    }
    if (currentCart.length === 1) {
      localStorage.setItem("cartServiceItemMainCategory", JSON.stringify(serviceMainCategory));
      if (item.serviceMerchantId) {
        const merchantDetails = JSON.parse(localStorage.getItem("serviceMerchantDetails") || "{}");
        if (merchantDetails && merchantDetails._id) {
          localStorage.setItem("cartServiceMerchantDetails", JSON.stringify(merchantDetails));
        }
      }
    }
    localStorage.setItem("serviceCartItems", JSON.stringify(currentCart));
    this.cartItems.next(currentCart);
  }

  /**
   * Removes a product variant from the cart.
   * If the specified product variant is in the cart, it will be removed; otherwise, the cart remains unchanged.
   * @param productId - The ID of the product to remove from the cart.
   * @param variantId - The ID of the variant to remove from the cart.
   */
  removeFromCart(productId: string, variant: any): void {
    const currentCart = JSON.parse(localStorage.getItem("serviceCartItems") || "[]").filter(
      (cartItem: Cart) =>
        !(
          cartItem.productId === productId &&
          cartItem.variantId === (typeof variant === "object" ? variant.variantId : variant) &&
          JSON.stringify(cartItem.modifiers) === JSON.stringify(variant.modifiers)
        )
    );
    if (currentCart.length === 0) {
      localStorage.removeItem("cartServiceItemMainCategory");
      if (localStorage.getItem("cartServiceMerchantDetails") !== "undefined") {
        localStorage.removeItem("cartServiceMerchantDetails");
      }
    }
    localStorage.setItem("serviceCartItems", JSON.stringify(currentCart));
    this.cartItems.next(currentCart);
  }

  /**
   * Updates the quantity of a product variant in the cart.
   * If the product variant is in the cart, the quantity will be updated to the given quantity,
   * up to the maximum allowed in the cart (maxQtyAddInCart).
   * If the product variant is not in the cart, the cart remains unchanged.
   * @param productId The ID of the product to update in the cart.
   * @param variantId The ID of the variant to update in the cart.
   * @param quantity The new quantity of the product variant in the cart.
   * @param maxQtyAddInCart The maximum quantity that can be added to the cart for this product variant.
   */
  updateQuantity(productId: string, variant: any, quantity: number, maxQtyAddInCart: number): void {
    const currentCart: Cart[] = JSON.parse(localStorage.getItem("serviceCartItems") || "[]");
    const itemIndex = currentCart.findIndex(
      (cartItem) =>
        cartItem.productId === productId &&
        cartItem.variantId === (typeof variant === "object" ? variant.variantId : variant) &&
        JSON.stringify(cartItem.modifiers) === JSON.stringify(variant.modifiers)
    );
    if (itemIndex !== -1) {
      // Update the quantity of the item (ensure it doesn't exceed maxQtyAddInCart)
      currentCart[itemIndex].qty = Math.min(quantity, maxQtyAddInCart);
      localStorage.setItem("serviceCartItems", JSON.stringify(currentCart));
      // Emit the updated cart
      this.cartItems.next(currentCart);
    }
  }

  /**
   * Fetches the cart data using the ApiService.
   * Subscribes to the service's observable to receive the cart data, updates the observer with the response data and completes the observable.
   * If there is an error, it updates the observer with false and completes the observable.
   * @param parameters The parameters to be passed as query parameters to the API.
   * @returns An observable of the cart data.
   */
  getCart(parameters: any): Observable<any> {
    return new Observable<any>((observer) => {
      const url = `${servicesApiCollections.cart}`;
      this.apiService.post({ url, parameters }).subscribe({
        next: (response) => {
          if (response.data) {
            observer.next(response.data.data);
            observer.complete();
          } else {
            observer.next(false);
            observer.complete();
          }
        },
        error: () => {
          observer.next(false);
          observer.complete();
        }
      });
    });
  }

  /**
   * Places an order using the ApiService.
   * Subscribes to the service's observable to receive the response data, updates the observer with the response data and completes the observable.
   * If there is an error, it updates the observer with false and completes the observable.
   * @param parameters The parameters to be passed as query parameters to the API.
   * @returns An observable of the response data.
   */
  placeOrder(parameters: any): Observable<any> {
    return new Observable<any>((observer) => {
      const url = `${servicesApiCollections.booking}`;
      this.apiService.post({ url: url, parameters }).subscribe({
        next: (response) => {
          if (response) {
            observer.next(response);
            observer.complete();
          } else {
            observer.next(false);
            observer.complete();
          }
        },
        error: () => {
          observer.next(false);
          observer.complete();
        }
      });
    });
  }

  /**
   * Fetches the list of service providers using the ApiService.
   * Subscribes to the service's observable to receive the provider data, updates the observer with the response data and completes the observable.
   * If there is an error, it updates the observer with false and completes the observable.
   * @param parameters The parameters to be passed as query parameters to the API.
   * @returns An observable of the provider data.
   */
  getServiceProviders(parameters: any): Observable<any> {
    return new Observable<any>((observer) => {
      const url = `${servicesApiCollections.serviceProviders}`;
      this.apiService.post({ url: url, parameters }).subscribe({
        next: (response) => {
          if (response.data) {
            observer.next(response.data.data);
            observer.complete();
          } else {
            observer.next(false);
            observer.complete();
          }
        },
        error: () => {
          observer.next(false);
          observer.complete();
        }
      });
    });
  }

  /**
   * Fetches the list of merchants using the ApiService.
   * Subscribes to the service's observable to receive the merchant data, updates the observer with the response data, and completes the observable.
   * If there is an error, it updates the observer with false and completes the observable.
   * @param parameters The parameters to be passed as query parameters to the API.
   * @returns An observable of the merchant data.
   */
  getMerchantList(parameters: any): Observable<any> {
    return new Observable<any>((observer) => {
      const url = `${servicesApiCollections.merchantList}`;
      this.apiService.post({ url: url, parameters }).subscribe({
        next: (response) => {
          if (response.data) {
            observer.next(response.data.data);
            observer.complete();
          } else {
            observer.next(false);
            observer.complete();
          }
        },
        error: () => {
          observer.next(false);
          observer.complete();
        }
      });
    });
  }

  /**
   * Fetches the details of a product using the ApiService.
   * Subscribes to the service's observable to receive the product data, updates the observer with the response data, and completes the observable.
   * If there is an error, it updates the observer with false and completes the observable.
   * @param parameters The parameters to be passed as query parameters to the API.
   * @param productId The ID of the product to fetch details for.
   * @returns An observable of the product data.
   */
  getProductDetails(parameters: any, productId: string): Observable<any> {
    return new Observable<any>((observer) => {
      const url = `${servicesApiCollections.productDetails}/${productId}`;
      this.apiService.get({ url: url, parameters }).subscribe({
        next: (response) => {
          if (response.data) {
            observer.next(response.data.data);
            observer.complete();
          } else {
            observer.next(false);
            observer.complete();
          }
        },
        error: () => {
          observer.next(false);
          observer.complete();
        }
      });
    });
  }

  /**
   * Updates the selected cart merchant observable with the given merchant.
   * This will cause all subscribers to the selectedCartMerchant$ observable to receive the new merchant.
   * @param merchant The merchant to select as the current cart merchant.
   */
  updateSelectedCartMerchant(merchant: Merchant): void {
    this.selectedCartMerchantSubject.next(merchant);
  }
}
